import { createAsyncThunk } from "@reduxjs/toolkit";
import AdminSettlementApiService, {
  SettleOutcome,
  classifySettleError,
  isAdminSettlementAccessDenied,
  toFriendlyAdminSettlementError,
} from "../apiServices/AdminSettlementApiService";
import {
  Settlement,
  SettlementRequest,
  SettlementRequestStatus,
} from "../../interfaces/OnboardingObject";
import { ADMIN_ACCESS_DENIED } from "./AdminBusinessesThunk";

// Thunks behind Admin -> Settlements. Same conventions as AdminBusinessesThunk:
// list failures reject with a friendly string (or the ADMIN_ACCESS_DENIED
// sentinel, rendered as a state). Settle rejects with a SettleFailure so the
// dialog knows whether it may stay open for a retry.

export const fetchAdminSettlementRequests = createAsyncThunk<
  { status: SettlementRequestStatus; rows: SettlementRequest[] },
  SettlementRequestStatus,
  { rejectValue: string }
>("admin/fetchSettlementRequests", async (status, { rejectWithValue }) => {
  try {
    const rows = await AdminSettlementApiService.listSettlementRequests(status);
    return { status, rows };
  } catch (error) {
    if (isAdminSettlementAccessDenied(error)) return rejectWithValue(ADMIN_ACCESS_DENIED);
    return rejectWithValue(toFriendlyAdminSettlementError(error));
  }
});

export interface SettleAdminRequestInput {
  request: SettlementRequest;
  reference?: string;
  note?: string;
}

export interface SettleFailure {
  outcome: SettleOutcome;
  message: string;
}

/**
 * Records that Nine paid `request` in full. On a stale or unknown outcome the
 * PENDING list is reloaded here, so the screen never offers Settle on a row
 * the server may already have closed.
 */
export const settleAdminSettlementRequest = createAsyncThunk<
  Settlement,
  SettleAdminRequestInput,
  { rejectValue: SettleFailure }
>("admin/settleSettlementRequest", async (input, { dispatch, rejectWithValue }) => {
  try {
    return await AdminSettlementApiService.settleRequest(input.request.requestId, {
      expectedAmount: input.request.amount,
      reference: input.reference,
      note: input.note,
    });
  } catch (error) {
    const failure = classifySettleError(error);
    if (failure.outcome !== "rejected") dispatch(fetchAdminSettlementRequests("PENDING"));
    return rejectWithValue(failure);
  }
});
